import { APP_INITIALIZER, ApplicationConfig, ErrorHandler, LOCALE_ID, inject, provideAppInitializer, provideBrowserGlobalErrorListeners, provideZoneChangeDetection } from '@angular/core';
import { provideRouter, RouteReuseStrategy, withComponentInputBinding, withNavigationErrorHandler } from '@angular/router';
import { provideHttpClient, withFetch, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { TranslateService, provideTranslateLoader, provideTranslateService } from '@ngx-translate/core';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalInterceptor,
  MsalService,
} from '@azure/msal-angular';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { routes } from './app.routes';
import { GlobalErrorHandler, tenantInterceptor, errorInterceptor, serverStatusInterceptor, recoverFromNavigationError, ParamAwareRouteReuseStrategy, localeIdFor, registerAppLocales } from '#core';
import { readCachedLanguage } from '#services';
import { LanguageLoader } from '#locale';
import { msalInstanceFactory, msalInterceptorConfigFactory, msalGuardConfigFactory } from '../core/auth/msal.config';

function msalInitializerFactory(msalService: MsalService): () => Promise<void> {
  return async () => {
    await msalService.instance.initialize();
    try {
      await firstValueFrom(msalService.handleRedirectObservable());
    } catch (error) {
      // A rejected initializer aborts bootstrap and leaves a blank page. MsalGuard calls
      // handleRedirectObservable again and will surface the failure through its own route.
      console.error('[MSAL] handleRedirect failed during startup', error);
    }
  };
}

registerAppLocales();

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      routes,
      withComponentInputBinding(),
      withNavigationErrorHandler(event => recoverFromNavigationError(event)),
    ),
    { provide: RouteReuseStrategy, useExisting: ParamAwareRouteReuseStrategy },
    // serverStatusInterceptor runs first so it can short-circuit calls while the API is unreachable.
    provideHttpClient(withFetch(), withInterceptors([serverStatusInterceptor, tenantInterceptor, errorInterceptor]), withInterceptorsFromDi()),
    provideAnimationsAsync(),
    // Dates, numbers and the datepicker follow the UI language. Fixed at boot; LanguageService reloads on a change.
    { provide: LOCALE_ID, useValue: localeIdFor(readCachedLanguage()) },
    provideNativeDateAdapter(),
    // Without this every field reserves a blank line under itself for a hint it may never show,
    // so the gap between two controls depended on whether the template remembered
    // `subscriptSizing="dynamic"`. The container's gap is the only spacing now.
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { subscriptSizing: 'dynamic' } },
    provideTranslateService({
      loader: provideTranslateLoader(LanguageLoader),
      lang: readCachedLanguage(),
      fallbackLang: 'en',
    }),
    // Other languages load as lazy chunks: hold the first render until the user's has arrived, so no screen
    // computes a label from a key that has not loaded yet.
    provideAppInitializer(() => firstValueFrom(inject(TranslateService).use(readCachedLanguage()))),
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    // MSAL
    { provide: MSAL_INSTANCE, useFactory: msalInstanceFactory },
    { provide: MSAL_INTERCEPTOR_CONFIG, useFactory: msalInterceptorConfigFactory },
    { provide: MSAL_GUARD_CONFIG, useFactory: msalGuardConfigFactory },
    { provide: HTTP_INTERCEPTORS, useClass: MsalInterceptor, multi: true },
    {
      provide: APP_INITIALIZER,
      useFactory: msalInitializerFactory,
      deps: [MsalService],
      multi: true,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
  ],
};
